import { Fetch, Response } from '@paychex/core/types/data';
import { Tracker } from '@paychex/core/types/trackers';
import {
    get,
    pick,
    mapKeys,
    defaults,
    cloneDeep,
} from 'lodash';

const TRACEABILITY_HEADERS = [
    'x-payx-bizpn',
    'x-payx-txid',
    'x-payx-reqid',
    'x-payx-cnsmr',
    'x-payx-comp',
    'x-payx-sid',
    'x-payx-user',
    'x-payx-user-untrusted',
    'x-payx-pvdr',
    'x-payx-svc',
    'x-payx-sts',
];

const CATEGORIES = [
    'UNKNOWN',
    'INFO',
    'SUCCESS',
    'REDIRECT',
    'CLIENT ERROR',
    'SERVER ERROR',
];

function stripPrefix(_: any, key: string): string {
    return key.replace('x-payx-', '');
}

function getPassFail(response: Response): string {
    if (!response) return 'Fail';
    return (response.status >= 200 && response.status < 300) ? 'Pass' : 'Fail';
}

function sluggify(text: string): string {
    return text && text.replace(/[\s=,]+/g, '-');
}

function getTransactionStatus(response: Response): string {
    if (!response) return 'NO RESPONSE';
    const category = CATEGORIES[Math.floor(response.status / 100)];
    return sluggify(`${category}:${response.statusText || 'OK'}`);
}

/**
 * Wraps a fetch method to send traceability headers and create traceability events.
 * You can opt out of traceability headers and events by setting `{ ignore: { traceability: true }}`
 * in your DataDefinition object.
 *
 * @param fetch The fetch function to wrap.
 * @param tracker The tracker to use to log traceability events.
 * @returns A fetch implementation that creates traceability events
 * for network calls.
 * @example
 * ```js
 * import tracker from '~/path/to/tracker.js';
 * import { fetch } from '~/path/to/datalayer.js';
 *
 * // data calls made through the pipeline will
 * // have traceability headers and create traceability
 * // events using the specified tracker
 * const pipeline = internal.data.utils.withTraceability(fetch, tracker);
 * ```
 */
export function withTraceability(fetch: Fetch, tracker: Tracker): Fetch {
    return async function useTraceability(request) {
        if (get(request, 'ignore.traceability', false))
            return await fetch(request);
        const start = Date.now();
        const timeStart = new Date(start).toISOString();
        const clone = cloneDeep(request);
        defaults(clone.headers, {
            'x-payx-reqid': tracker.uuid(),
            'x-payx-bizpn': get(clone, 'path', clone.url),
            'x-payx-comp': '@paychex/internal',
            'x-payx-cnsmr': 'PaychexFlex',
            'x-payx-txid': tracker.uuid(),
        });
        let response;
        let headers = get(clone, 'headers', {});
        headers = pick(headers, TRACEABILITY_HEADERS);
        headers = mapKeys(headers, stripPrefix);
        headers = {
            ...headers,
            time: timeStart,
            category: 'TRACEABILITY',
        };
        tracker.event('TRACEABILITY-REQUEST', {
            mark: 'txst',
            ...headers,
            cnsmr: undefined,
            reqid: undefined
        });
        tracker.event('TRACEABILITY-REQUEST', {
            mark: 'reqint',
            svc: clone.url,
            ...headers,
            cnsmr: undefined,
        });
        try {
            response = await fetch(clone);
        } catch (e) {
            response = e.response;
            throw e;
        } finally {
            const stop = Date.now();
            const timeEnd = new Date(stop).toISOString();
            headers = {
                ...get(clone, 'headers', {}),
                ...get(response, 'meta.headers', {})
            };
            headers = pick(headers, TRACEABILITY_HEADERS);
            headers = mapKeys(headers, stripPrefix);
            headers = {
                ...headers,
                time: timeEnd,
                category: 'TRACEABILITY',
                duration: stop - start,
            }
            tracker.event('TRACEABILITY-RESPONSE', {
                mark: 'rsprecd',
                sts: getPassFail(response),
                pvdr: '@paychex/internal',
                svc: clone.url,
                ...headers,
                cnsmr: undefined,
            });
            tracker.event('TRACEABILITY-RESPONSE', {
                mark: 'txend',
                sts: getPassFail(response),
                sts_text: getTransactionStatus(response),
                ...headers,
                svc: undefined,
                pvdr: undefined,
                cnsmr: undefined,
                reqid: undefined
            });
        }
        return response;
    } as Fetch;
}
